import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { validate } from 'email-validator';
import { useLogin } from '@adapters/bigcommerce/hooks/customer';
import ReCAPTCHA from 'react-google-recaptcha';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';

const useLoginForm = (
  onLogin?: (customerGroupId?: number) => void,
  isCheckout = false
) => {
  const { t } = useTranslation('common');
  const { customer } = useCustomer();

  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const login = useLogin();

  useEffect(() => {
    setEmailIsValid(validate(email));
  }, [email]);

  useEffect(() => {
    setPasswordIsValid(!!password.length);
  }, [password]);

  const handleValidation = useCallback(() => {
    const isValid = emailIsValid && passwordIsValid;

    if (dirty) {
      setDisabled(!isValid);
    }

    return isValid;
  }, [emailIsValid, passwordIsValid, dirty]);

  useEffect(() => {
    handleValidation();
  }, [handleValidation]);

  const handleLogin = async (
    recaptchaRef: ReCAPTCHA | null,
    e: React.SyntheticEvent<EventTarget>
  ) => {
    e.preventDefault();

    if (!dirty) {
      setDirty(true);
    }

    const isValid = handleValidation();
    if (!isValid) return;

    try {
      const recaptchaToken = await recaptchaRef?.executeAsync();
      recaptchaRef?.reset();

      setLoading(true);
      setErrorMessage('');
      await login({
        email,
        password,
        recaptchaToken,
      });
      if (isCheckout) setLoading(false);
      if (onLogin) onLogin(customer?.customerGroupId);
    } catch (error: any) {
      const message = error.errors?.[0].code
        ? t(`login.error.${error.errors[0].code}`)
        : t('login.error.unknown.error');
      console.error(`login form error: ${message}`);

      setErrorMessage(message);
      setLoading(false);
    }
  };

  return {
    email: {
      value: email,
      set: setEmail,
      isValid: emailIsValid,
    },
    password: {
      value: password,
      set: setPassword,
      isValid: passwordIsValid,
    },
    submitDisabled: disabled,
    isLoading: loading,
    isDisabled: disabled,
    isDirty: dirty,
    errorMessage,
    onSubmit: handleLogin,
  };
};

export default useLoginForm;
